/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { useQuery, usePrefetchQuery } from '@tanstack/react-query';

import {
    getNextWeekend,
    useOrderFacilityData,
} from '../components/CampgroundRecommendations/CampgroundRecommendations.utils';
import { getCampgroundRecommendations } from './recommendations-api';
import { getAggregateReviews, getAIReviewSummary } from './rating-review-api';

export const GET_CAMPGROUND_RECOMMENDATIONS = 'GET_CAMPGROUND_RECOMMENDATIONS';

export function useGetAggregateReviews({
    locationId,
    locationType,
}: {
    locationId: string;
    locationType: string;
}) {
    return useQuery({
        queryKey: ['aggregate-reviews', locationId, locationType],
        queryFn: () => getAggregateReviews({ locationId, locationType }),
        enabled: !!locationId && !!locationType,
        staleTime: Infinity,
        refetchOnWindowFocus: false,
    });
}

export function useGetAIReviewSummary({
    locationId,
    locationType,
}: {
    locationId: string;
    locationType: string;
}) {
    return useQuery({
        queryKey: ['ai-review-summary', locationId, locationType],
        queryFn: () => getAIReviewSummary({ locationId, locationType }),
        enabled: !!locationId && !!locationType,
        staleTime: Infinity,
        refetchOnWindowFocus: false,
        // The AI summary can legitimately 404 for new facilities — don't
        // hammer the endpoint with retries; the component treats absent
        // summary as a soft empty state.
        retry: false,
    });
}

export function useGetCampgroundRecommendations({
    campgroundId,
    startDate,
    endDate,
}: {
    campgroundId: string;
    startDate: string; // 2025-05-09T00:00:00.000Z
    endDate: string; // 2025-05-11T00:00:00.000Z
}) {
    return useQuery({
        queryKey: [GET_CAMPGROUND_RECOMMENDATIONS, { campgroundId, startDate, endDate }],
        queryFn: async ({ signal }) =>
            getCampgroundRecommendations(signal, { campgroundId, startDate, endDate }),
        staleTime: 10 * 60 * 1000, // 10 minutes,
        refetchOnWindowFocus: false,
        retry: 1,
    });
}

export function usePrefetchCampgroundRecommendations() {
    const { campgroundId } = useOrderFacilityData();

    if (!campgroundId) {
        return;
    }
    const [startDate, endDate] = getNextWeekend();

    // This should be ok because this is the last hook used and the whole thing is pretty simple
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return usePrefetchQuery({
        queryKey: [GET_CAMPGROUND_RECOMMENDATIONS, { campgroundId, startDate, endDate }],
        queryFn: async ({ signal }) =>
            getCampgroundRecommendations(signal, {
                campgroundId,
                startDate: startDate.toISOString(),
                endDate: endDate.toISOString(),
            }),
        staleTime: 10 * 60 * 1000, // 10 minutes,
        retry: 1,
    });
}
